import { Component, OnInit, inject } from '@angular/core';
import { ClientPrescriberTask } from 'app/core/models/client-prescriber-task.model';
import { LimitOffsetPaging } from 'app/core/models/paged-results/limit-offset-paging.model';
import {
  ClientPrescriberTasksService,
  PagedClientPrescriberTasks,
} from 'app/core/services/client-prescriber-tasks.service';
import { TaskFilters, TaskQueueSettings } from '../../../core/models/user-settings/task-settings.model';
import { UserSettings } from '../../../core/enums/user-settings.enum';
import { UserSettingsService } from '../../../core/services/user-settings.service';
import { ClientsService } from 'app/core/services/clients.service';
import { ClientPrescriberTaskStatus } from 'app/core/enums/client-prescriber-task-status.enum';
import { displayTaskStatus } from 'app/core/options/task-status.opts';
import { ClientTasksFilterComponent } from '../../../shared/components/client-tasks-filter/client-tasks-filter.component';
import { DecimalPipe, CurrencyPipe } from '@angular/common';
import { ClientPrescriberTaskListComponent } from '../../components/client-prescriber-task-list/client-prescriber-task-list.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-capture-tasks',
    templateUrl: './capture-admin-client-prescriber-tasks.component.html',
    styleUrls: ['./capture-admin-client-prescriber-tasks.component.scss'],
    imports: [ClientTasksFilterComponent,
              ClientPrescriberTaskListComponent,
              MatProgressSpinner,
              DecimalPipe,
              CurrencyPipe]
})
export class CaptureAdminClientPrescriberTasksComponent implements OnInit {
  private clientPrescriberTasksService = inject(ClientPrescriberTasksService);
  private userSettingsService = inject(UserSettingsService);
  private clientsService = inject(ClientsService);

  tasks: ClientPrescriberTask[] = [];
  paging: LimitOffsetPaging;
  filters: TaskFilters = new TaskFilters();

  taskStatuses = [];
  specialties = [];
  manufacturers = [];
  sources = [];
  clients = [];

  clientTasksVisibleCount = null;
  clientTasksMinValue = null;

  loading = true;
  exportUrl = null;

  ngOnInit() {
    this.init();
  }

  onPageSizeChange(size) {
    this.paging.pageSize = size;
    this.loadTasks(this.paging.currentPage);
  }

  onPageChange(page) {
    this.loadTasks(page);
  }

  filtersChange(filters) {
    this.filters = filters;
    this.loadTasks(1);
    this.saveQueueSettings();
  }

  onClearAllFilters($event) {
    $event.preventDefault();
    this.filters = new TaskFilters();
    this.saveQueueSettings();
    this.loadTasks(this.paging.currentPage);
  }

  private init() {
    this.paging = LimitOffsetPaging.empty;
    this.loadQueueSettings();

    this.loadStatuses();
    this.loadSpecialties();
    this.loadManufacturers();
    this.loadSources();
    this.loadClients();

    this.loadTasks(1);
  }

  private loadSpecialties() {
    this.clientsService.getSpecialties().subscribe(result => {
      this.specialties = result;
    });
  }

  private loadManufacturers() {
    this.clientPrescriberTasksService.getManufacturers().subscribe(result => {
      this.manufacturers = result;
    });
  }

  private loadSources() {
    this.clientPrescriberTasksService.getSources().subscribe(result => {
      this.sources = result;
    });
  }

  private loadStatuses() {
    this.taskStatuses = Object.values(ClientPrescriberTaskStatus).map(v => ({
      value: v,
      display: displayTaskStatus(v),
    }));
  }

  private loadClients() {
    this.clientsService.getList(null, { applicableToReferralClaims: true }).subscribe(result => {
      this.clients = result.clients;
    });
  }

  private loadTasks(page) {
    this.loading = true;
    const paging = { page, pageSize: this.paging.pageSize };

    this.clientPrescriberTasksService.getList(paging, this.filters).subscribe((result: PagedClientPrescriberTasks) => {
      this.tasks = result.clientPrescriberTasks;
      this.paging = result.meta.paging;
      this.clientTasksVisibleCount = result.meta.clientTasksVisibleCount;
      this.clientTasksMinValue = result.meta.clientTasksMinValue;
      this.loading = false;
    });
  }

  private loadQueueSettings() {
    const queueSettings: TaskQueueSettings = this.userSettingsService.get<TaskQueueSettings>(
      UserSettings.captureAdminClientPrescriberTaskQueueSettings
    );

    if (queueSettings) {
      this.paging = queueSettings.paging;
      this.filters = queueSettings.filters;
    }
  }

  private saveQueueSettings(position = null) {
    const queueSettings = new TaskQueueSettings();
    queueSettings.position = position;
    queueSettings.paging = this.paging;
    queueSettings.filters = this.filters;
    this.userSettingsService.save<TaskQueueSettings>(
      UserSettings.captureAdminClientPrescriberTaskQueueSettings,
      queueSettings
    );
  }
}
